import { AlignCenter, AlignLeft, AlignRight, AlignVerticalJustifyCenter, AlignVerticalJustifyEnd, AlignVerticalJustifyStart, ChevronDown } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import type { TextTypography } from "@/document/model/documentStyles";
import { loadGoogleFontWeights, weightsForGoogleFont } from "@/lib/GoogleFontLoader";
import { pickNearestFontWeight } from "@/lib/fontWeightLabels";
import { FontPickerPopover } from "./FontPickerPopover";
import { FontWeightLabel } from "./FontWeightLabel";
import { LineHeightField } from "./LineHeightField";
import { FontSizeField } from "./FontSizeField";
import { PanelSelect, PanelSelectItem } from "./PanelSelect";
import { NumberField } from "./NumberField";

export function StyleTypographyFields({
  typography,
  onChange,
}: {
  typography: TextTypography;
  onChange: (typography: TextTypography) => void;
}) {
  const [fontPickerOpen, setFontPickerOpen] = useState(false);
  const [availableWeights, setAvailableWeights] = useState<number[]>(() => weightsForGoogleFont(typography.fontFamily));

  useEffect(() => {
    let cancelled = false;
    setAvailableWeights(weightsForGoogleFont(typography.fontFamily));
    void loadGoogleFontWeights(typography.fontFamily).then(weights => {
      if (!cancelled) {
        setAvailableWeights(weights);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [typography.fontFamily]);

  const patchTypography = useCallback((patch: Partial<TextTypography>) => {
    onChange({ ...typography, ...patch });
  }, [onChange, typography]);

  const commitFont = useCallback((family: string) => {
    void loadGoogleFontWeights(family).then(weights => {
      onChange({
        ...typography,
        fontFamily: family,
        fontWeight: pickNearestFontWeight(typography.fontWeight, weights),
      });
      setFontPickerOpen(false);
    });
  }, [onChange, typography]);

  return (
    <>
      <button
        type="button"
        onClick={() => setFontPickerOpen(true)}
        className="flex h-8 w-full items-center justify-between rounded border border-white/10 bg-white/[0.04] px-2 text-left text-[12px] text-slate-200 hover:bg-white/[0.07]"
      >
        <span className="min-w-0 truncate">{typography.fontFamily}</span>
        <ChevronDown className="size-3.5 shrink-0 text-slate-500" aria-hidden="true" />
      </button>

      <div className="grid grid-cols-2 gap-2">
        <PanelSelect
          value={String(typography.fontWeight)}
          onValueChange={value => patchTypography({ fontWeight: Number(value) })}
        >
          {availableWeights.map(weight => (
            <PanelSelectItem key={weight} value={String(weight)}>
              <FontWeightLabel weight={weight} />
            </PanelSelectItem>
          ))}
        </PanelSelect>
        <FontSizeField
          value={typography.fontSize}
          fallbackValue={typography.fontSize}
          min={1}
          max={400}
          onCommit={fontSize => patchTypography({ fontSize })}
        />
        <LineHeightField
          lineHeight={typography.lineHeight}
          fontSize={typography.fontSize}
          onCommit={lineHeight => patchTypography({ lineHeight })}
        />
        <NumberField
          label="Track"
          value={typography.letterSpacing}
          min={-50}
          max={200}
          step={0.1}
          onCommit={letterSpacing => patchTypography({ letterSpacing })}
        />
      </div>

      <div className="grid grid-cols-6 gap-1">
        <AlignButton active={typography.horizontalAlign === "LEFT"} label="Align left" onClick={() => patchTypography({ horizontalAlign: "LEFT" })} icon={<AlignLeft className="size-4" />} />
        <AlignButton active={typography.horizontalAlign === "CENTER"} label="Align center" onClick={() => patchTypography({ horizontalAlign: "CENTER" })} icon={<AlignCenter className="size-4" />} />
        <AlignButton active={typography.horizontalAlign === "RIGHT"} label="Align right" onClick={() => patchTypography({ horizontalAlign: "RIGHT" })} icon={<AlignRight className="size-4" />} />
        <AlignButton active={typography.verticalAlign === "TOP"} label="Align top" onClick={() => patchTypography({ verticalAlign: "TOP" })} icon={<AlignVerticalJustifyStart className="size-4" />} />
        <AlignButton active={typography.verticalAlign === "CENTER"} label="Align middle" onClick={() => patchTypography({ verticalAlign: "CENTER" })} icon={<AlignVerticalJustifyCenter className="size-4" />} />
        <AlignButton active={typography.verticalAlign === "BOTTOM"} label="Align bottom" onClick={() => patchTypography({ verticalAlign: "BOTTOM" })} icon={<AlignVerticalJustifyEnd className="size-4" />} />
      </div>

      {fontPickerOpen ? (
        <FontPickerPopover
          appliedFamily={typography.fontFamily}
          onPreview={family => {
            void loadGoogleFontWeights(family);
          }}
          onCommit={commitFont}
          onClose={() => setFontPickerOpen(false)}
        />
      ) : null}
    </>
  );
}

function AlignButton({
  active,
  label,
  icon,
  onClick,
}: {
  active: boolean;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon-sm"
      title={label}
      aria-label={label}
      onClick={onClick}
      className={active ? "bg-sky-500/20 text-sky-100" : "bg-white/[0.04] text-slate-300 hover:bg-white/[0.07]"}
    >
      {icon}
    </Button>
  );
}
